import fetchJson from "@/lib/fetchJson";
import useSWR from "swr";
import { isValidId } from "@/lib/util";

interface CreatorQuery {
  creator: Creator;
  budgets: CreatorBudget[];
  posts: Post[];
}

interface PostQuery {
  posts: Post[]
}

export const useCreators = () => useSWR<Creator[]>(`/creator`, fetchJson);

export const useCreator = (id: string | undefined) =>
  useSWR<CreatorQuery>(isValidId(id) ? `/creator/${id}` : null, fetchJson);

interface CreatorSearchResult {
  creators: Creator[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export const useCreatorSearch = (query: string = '', platform: string = '', enabled: boolean = true) => {
  const params = new URLSearchParams();
  if (query) params.append('q', query);
  if (platform) params.append('platform', platform);
  params.append('limit', '20');
  
  return useSWR<CreatorSearchResult>(
    enabled && (query.length >= 2 || platform) ? `/creator/search?${params.toString()}` : null,
    fetchJson
  );
};

  export const useCreatorPosts = (id: string | undefined) =>
  useSWR<PostQuery>(isValidId(id) ? `/creator/${id}/posts` : null, fetchJson);

  export const getCreatorPosts = async (id: string) =>
  fetchJson(`/creator/${id}/posts`, {
    method: "GET",
  });

export const useCreatorVibes = () =>
  useSWR<CreatorVibe[]>("/creator/vibe", fetchJson);

export interface PerformanceRanking {
  rank: number;
  creatorPriceId: number;
  creatorId: number;
  username: string;
  platform: string;
  creatorName: string;
  averageViews: number | null;
  recentPostCount: number | null;
  lastScraped: string | null;
  engagement: number | null;
  followers: number | null;
  price: number;
  starred: boolean;
  avatar: string | null;
  postFormat: string | null;
  themes: string[];
  vibes: string[];
  alreadyBooked?: boolean;
  hasEngagement?: boolean;
}

export interface PerformanceRankingsResponse {
  rankings: PerformanceRanking[];
  total: number;
  platform: string;
}

export const usePerformanceRankings = (params: URLSearchParams | null) =>
  useSWR<PerformanceRankingsResponse>(
    params ? `/creator/performance-rankings?${params.toString()}` : null,
    fetchJson,
    { refreshInterval: 30000 },
  );

export const refreshPerformanceRankings = async (
  params: URLSearchParams,
) =>
  fetchJson(`/creator/performance-rankings/refresh?${params.toString()}`, {
    method: "POST",
    body: JSON.stringify({}),
  });

export const useCreatorTags = () =>
  useSWR<CreatorTag[]>("/creator/tags", fetchJson);

export const useCreatorThemes = () =>
  useSWR<CreatorTheme[]>("/creator/theme", fetchJson);

export const deleteTag = async (id: string) =>
  fetchJson(`/creator/tags/${id}`, {
    method: "DELETE",
  });

export const deleteTheme = async (id: string) =>
  fetchJson(`/creator/theme/${id}`, {
    method: "DELETE",
  });

  export const deleteVibe = async (id: string) =>
  fetchJson(`/creator/vibe/${id}`, {
    method: "DELETE",
  });

export const deleteBudget = (id: string) =>
  fetchJson(`/creator/budget/${id}`, {
    method: "DELETE",
  });

export const updateCreator = async (id: string, data: any) =>
  fetchJson(`/creator/${id}`, {
    method: "POST",
    body: JSON.stringify(data),
  });
